import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FINANCE_API_CONFIGURED } from '../lib/family-finance-api'
import OverviewTab from '../components/family-finance/OverviewTab'
import AccountsTab from '../components/family-finance/AccountsTab'
import InvestmentsTab from '../components/family-finance/InvestmentsTab'
import PropertiesTab from '../components/family-finance/PropertiesTab'
import BudgetTab from '../components/family-finance/BudgetTab'
import BillsTab from '../components/family-finance/BillsTab'
import TaxesTab from '../components/family-finance/TaxesTab'
import EntitiesTab from '../components/family-finance/EntitiesTab'
import DocumentsTab from '../components/family-finance/DocumentsTab'

const TABS = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'accounts', label: 'Accounts', icon: '🏦' },
  { id: 'investments', label: 'Investments', icon: '📈' },
  { id: 'properties', label: 'Properties', icon: '🏠' },
  { id: 'budget', label: 'Budget', icon: '🧮' },
  { id: 'bills', label: 'Bills', icon: '🔔' },
  { id: 'taxes', label: 'Taxes', icon: '🧾' },
  { id: 'entities', label: 'Businesses', icon: '🏛️' },
  { id: 'documents', label: 'Documents', icon: '🗂️' },
] as const

type TabId = (typeof TABS)[number]['id']

export default function FamilyFinancePage() {
  const [tab, setTab] = useState<TabId>('overview')

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <span className="text-3xl">💰</span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Finances</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Investments, taxes, budgets, bills, business entities, and documents — all in one place
          </p>
        </div>
      </div>

      {!FINANCE_API_CONFIGURED && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
          <span className="text-red-600 dark:text-red-400 mt-0.5">⚠️</span>
          <div>
            <p className="text-red-600 dark:text-red-400 text-sm font-medium">Finances API not configured</p>
            <p className="text-red-600/80 dark:text-red-400/80 text-xs mt-0.5">
              This build has no <code>VITE_FAMILY_API_URL</code>, so no account, balance, or tax data can load. Nothing
              is shown in its place on purpose — a stand-in figure that looks real is worse than an empty screen.
            </p>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.id
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
            }`}
          >
            <span className="mr-1.5">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab onGoToTab={(t) => setTab(t as TabId)} />}
      {tab === 'accounts' && <AccountsTab />}
      {tab === 'investments' && <InvestmentsTab />}
      {tab === 'properties' && <PropertiesTab />}
      {tab === 'budget' && <BudgetTab />}
      {tab === 'bills' && <BillsTab />}
      {tab === 'taxes' && <TaxesTab />}
      {tab === 'entities' && <EntitiesTab />}
      {tab === 'documents' && <DocumentsTab />}
    </div>
  )
}
